import {ICalendarDate} from "@pg-design/calendar-module";
import {isNumber} from "@pg-mono/nodash";
import {UserComEventNames} from "@pg-mono/user-com";
import {Currency} from "@pg-rp/common-types";

import {getClientStore} from "../../../../client/utils/get_client_store";
import {createRpLeadPerformanceDebugTrace} from "../../../app/performance_debug";
import {authSucceeded} from "../../../auth/actions/auth_succeeded_event";
import {AuthActionType} from "../../../auth/types/AuthActionType";
import {addAppliedOffers} from "../../../offer/map/actions/add_applied_offers";
import {hitGTMEcommercePurchase} from "../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_purchase";
import {IOfferDetailApplication} from "../../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../../property/types/IPropertyDetailApplication";
import {IAppDispatch} from "../../../store/store";
import {awinHit} from "../../../tracking/awin_tracking/awin_conversion_tag";
import {facebookPixelPurchase} from "../../../tracking/facebook_pixel/facebook_pixel_purchase";
import {gtmEventApplicationRegister} from "../../../tracking/google_tag_manager/gtm_event_application_register";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {patchUserProfileFromApplication} from "../../../user/actions/patch_user_profile_from_application";
import {IUserProfile} from "../../../user/types/IUserProfile";
import {ViewType} from "../../../view_type/ViewType";
import {afterPostApplicationActions} from "../../actions/after_post_application";
import {createApplicationApi} from "../../api/create_application";
import {trackAmplitudeLeadFormGenerateLead} from "../../tracking/amplitude/track_amplitude_lead_form_events";
import {IApplicationAnalyticsData, sendApplicationHit} from "../../tracking/application_form_tracking";
import {gtmInquiryOnApplicationSuccess} from "../../tracking/lead/gtm_inquiry_on_application_success";
import {hitLeadSuccess} from "../../tracking/lead/hit_lead_success";
import {IApplicationAdditionalQuestionTypes} from "../../types/form/IApplicationFormAdditionalQuestionTypes";
import {IApplicationOptions} from "../../types/form/IApplicationFormOptions";
import {IApplicationFormValues} from "../../types/form/IApplicationFormValues";
import {IApplicationData} from "../../types/IApplicationData";
import {IAmplitudeLeadFormSubmitContext, IAmplitudeLeadFormTrackingContext} from "../../types/lead/IAmplitudeLeadFormEventTracking";
import {IHitApplicationData} from "../../types/lead/IHitApplicationData";
import {ILeadModalLeadType} from "../../types/lead/ILeadModalLeadType";
import {ILeadType} from "../../types/lead/ILeadType";
import {IPostApplicationPayload} from "../../types/lead/IPostApplicationPayload";
import {IRealEstateIds} from "../../types/lead/IRealEstateIds";
import {addPropertiesToStorageSentApplication} from "../application_property_storage";
import {ApplicationSource, ApplicationSourceSection} from "../form/application_form_source";
import {handlePostApplicationResponse} from "../form/handle_post_application_response";
import {addOffersToStorageSentApplication} from "../multi_lead/get_storage_sent_applications_id_list_by";
import {getParsedLeadFormValues} from "./get_parsed_lead_form_values";
import {lead} from "./lead";
import {updateUserComAfterLead} from "./update_user_com_after_lead";

export type ISendLeadParams = {
    leadData: IHitApplicationData;
    leadFormValues: IApplicationFormValues;
    leadMeta: ILeadMeta;
    isAuthenticated: boolean;
    userProfileData?: IUserProfile | null;
    senderState: {
        /**
         * TODO generatedMessage is not being generate in Application anymore,
         * but i'm not removing it since it's used as an input in other usages
         */
        generatedMessage: string | null;
        questions: IApplicationAdditionalQuestionTypes[];
    };
    analyticsData: IApplicationAnalyticsData;
    analyticsParams?: {
        customSourceSection?: string;
        amplitudeLeadFormEventTracking?: IAmplitudeLeadFormTrackingContext | null;
        amplitudeLeadFormSubmitContext?: IAmplitudeLeadFormSubmitContext;
    };
    affiliation?: string;
    realEstateIds?: IRealEstateIds;
    postOptions?: IApplicationOptions;
    creditData?: {price: number; contribution: number; period: number} | null;
    onSendSuccess?: (response: IApplicationData) => void;
    showSuccessNotification?: boolean;
    leadType: ILeadModalLeadType;
    dispatch: IAppDispatch;
};

export interface ILeadMeta {
    source: ApplicationSource;
    sourceSection: ApplicationSourceSection;
    locationHash: string;
    viewType: ViewType | null;
}

export async function sendLead(params: ISendLeadParams) {
    const {
        leadFormValues,
        leadMeta,
        leadData,
        isAuthenticated,
        senderState,
        analyticsData,
        analyticsParams,
        affiliation,
        postOptions,
        onSendSuccess,
        realEstateIds,
        userProfileData,
        creditData,
        showSuccessNotification = true,
        leadType,
        dispatch
    } = params;
    const trace = createRpLeadPerformanceDebugTrace("rp_send_lead", {
        leadModalType: leadType,
        leadType: lead.getLeadType(realEstateIds?.vendorId || null, realEstateIds?.offerId || null, realEstateIds?.propertyId || null),
        offerId: realEstateIds?.offerId,
        propertyId: realEstateIds?.propertyId,
        source: leadMeta.source,
        sourceSection: leadMeta.sourceSection,
        vendorId: realEstateIds?.vendorId
    });

    const {offer, property} = leadData;

    trace.mark("payload:build_start");
    const leadPayload = getLeadPayloadData({
        leadFormValues,
        leadMeta,
        offer,
        realEstateIds,
        creditData
    });
    trace.mark("payload:build_done", {
        hasFastcall: Boolean(leadPayload.fastcall),
        hasFinancingServices: Boolean(leadPayload.financing_services),
        hasOffer: typeof leadPayload.offer === "number",
        hasProperty: typeof leadPayload.property === "number",
        hasVendor: typeof leadPayload.vendor === "number"
    });

    trace.mark("create_application:dispatch_start");
    const applicationResponse = await dispatch(createApplicationApi.endpoints.createApplication.initiate(leadPayload));
    trace.mark("create_application:dispatch_done", {
        hasError: "error" in applicationResponse,
        hasData: "data" in applicationResponse
    });

    trace.mark("post_application_response:handle_start", {hasPostOptions: Boolean(postOptions)});
    const sentLeadRes = postOptions && (await handlePostApplicationResponse(applicationResponse, 1, dispatch, leadPayload, postOptions));
    trace.mark("post_application_response:handle_done", {hasSentLeadResponse: Boolean(sentLeadRes)});

    if (sentLeadRes && onSendSuccess) {
        trace.mark("on_send_success:start");
        onSendSuccess(sentLeadRes);
        trace.mark("on_send_success:done");
    }

    if (sentLeadRes) {
        const {generatedMessage, questions} = senderState;

        trace.mark("send_success_actions:start");
        await handleSendSuccess({
            response: sentLeadRes,
            offer: offer?.detail,
            property: property?.detail,
            isAuthenticated,
            userProfileData,
            showSuccessNotification
        });
        trace.mark("send_success_actions:done", {
            isAuthenticated,
            userCreated: Boolean(sentLeadRes.user_created)
        });

        trace.mark("lead_tracking:start");
        handleLeadTracking({
            name: leadFormValues.name,
            /*
                TODO: Create separate interface for applicationPost, so we won't have to use Partial<IApplication>
                    this will enable to remove "as string" below and will be a better approach in general
                    Application serializer to use for that - python-pg/-/blob/master/rpm/applications/serializers.py
             */
            phone: leadPayload.phone as string,
            email: leadFormValues.email,
            user_id: userProfileData?.uuid || null,
            rooms: leadFormValues.rooms,
            generatedMessage,
            questions,
            analyticsData,
            analyticsParams,
            originalText: leadFormValues.text || "",
            leadResponse: sentLeadRes,
            leadData,
            leadMeta,
            leadType: lead.getLeadType(realEstateIds?.vendorId || null, realEstateIds?.offerId || null, realEstateIds?.propertyId || null),
            leadModalType: leadType,
            affiliation,
            negotiation: leadFormValues.negotiation,
            meetingDay: leadFormValues.meetingDay,
            additionalQuestions: leadFormValues.additionalQuestions
        });
        trace.mark("lead_tracking:done");

        trace.mark("user_com_update:start");
        updateUserComAfterLead({
            eventName: UserComEventNames.UserLead,
            sentLead: {
                email: leadFormValues.email,
                phone: leadPayload.phone as string
            },
            userProfile: userProfileData
        });
        trace.mark("user_com_update:done");

        trace.mark("awin_hit:start");
        awinHit({
            applicationId: sentLeadRes.uuid,
            currency: offer?.detail?.currency || property?.detail?.currency || Currency.PLN
        });
        trace.mark("awin_hit:done");
    }

    trace.end("send_lead:done", {hasSentLeadResponse: Boolean(sentLeadRes)});
    return sentLeadRes;
}

interface IGetLeadPayloadParams {
    leadFormValues: IApplicationFormValues;
    leadMeta: ILeadMeta;
    offer: IHitApplicationData["offer"];
    realEstateIds?: IRealEstateIds;
    creditData?: {price: number; contribution: number; period: number} | null;
}

function getLeadPayloadData(params: IGetLeadPayloadParams) {
    const {leadFormValues, leadMeta, offer, realEstateIds, creditData} = params;
    const {source, sourceSection} = leadMeta;

    const parsedLeadFormValues = getParsedLeadFormValues(leadFormValues);

    const applicationData: IPostApplicationPayload = {
        source,
        source_section: sourceSection,
        ...parsedLeadFormValues
    };

    if (creditData) {
        applicationData.financing_services = true;
        applicationData.term_of_the_loan = creditData.period;
        applicationData.calculator_estimated_property_price = creditData.price;
        applicationData.own_contribution = creditData.contribution;
    }

    if (realEstateIds && realEstateIds.salesOfficeId) {
        // variant: 1 disables backend validation of 'meeting_time_preference' field
        applicationData.variant = 1;
        applicationData.sales_office = realEstateIds.salesOfficeId;
    }

    if (source === ApplicationSource.ApplicationPropertyScheduleMeeting) {
        // variant: 2 disables backend validation of 'sales_office' and 'meeting_time_preference' fields
        applicationData.variant = 2;
    }

    if (realEstateIds && isNumber(realEstateIds.propertyId)) {
        applicationData.property = realEstateIds.propertyId;
    } else if (realEstateIds && isNumber(realEstateIds.offerId)) {
        applicationData.offer = realEstateIds.offerId;
    } else if (realEstateIds && isNumber(realEstateIds.vendorId)) {
        applicationData.vendor = realEstateIds.vendorId;
    }

    if (offer?.detail?.configuration?.fastcall_uid) {
        applicationData.fastcall = true;
    }

    return applicationData;
}

async function handleSendSuccess(props: {
    response: IApplicationData;
    offer?: Pick<IOfferDetailApplication, "id" | "geo_point" | "region">;
    property?: Pick<IPropertyDetailApplication, "id">;
    isAuthenticated: boolean;
    userProfileData?: IUserProfile | null;
    showSuccessNotification: boolean;
}) {
    const store = getClientStore();

    if (!store) {
        throw new Error("sendLead: store is not available");
    }

    if (props.showSuccessNotification) {
        afterPostApplicationActions()(store.dispatch);
    }

    if (props.offer) {
        addOffersToStorageSentApplication([
            {
                id: props.offer.id,
                coordinates: props.offer.geo_point.coordinates,
                country: props.offer.region.country,
                regionId: props.offer.region.parent?.id
            }
        ]);
    }

    if (props.property) {
        addPropertiesToStorageSentApplication([props.property.id]);
    }

    // user was created when submitting the application - try to log in
    if (props.response.user_created) {
        store.dispatch(
            authSucceeded({
                type: AuthActionType.REGISTRATION,
                userSource: UserSource.APPLICATION,
                registrationMethod: "email"
            })
        );
    }
    // try to update logged-in user data
    if (props.isAuthenticated && props.userProfileData && !props.response.user_created) {
        await store.dispatch(patchUserProfileFromApplication(props.userProfileData, props.response));
    }

    if (props.isAuthenticated) {
        store.dispatch(addAppliedOffers([props.response.offer]));
    }
}

/**
 * Handles tracking after successful lead post/submit
 *
 * @param trackingParams
 * questions - form questions, but from component state, not the form itself
 * generatedMessage - a "rotating" message, but from component state, not the form itself
 * originalText - not modified text, taken directly from form values
 */
function handleLeadTracking(trackingParams: {
    name: string;
    phone: string;
    email: string;
    user_id: string | null;
    rooms?: number[];
    questions: IApplicationAdditionalQuestionTypes[];
    generatedMessage: string | null;
    originalText: string;
    analyticsData: IApplicationAnalyticsData;
    analyticsParams?: ISendLeadParams["analyticsParams"];
    leadResponse: IApplicationData;
    leadData: IHitApplicationData;
    leadMeta: ILeadMeta;
    leadType: ILeadType;
    leadModalType: ILeadModalLeadType;
    affiliation?: string;
    negotiation?: number | null;
    meetingDay?: ICalendarDate;
    additionalQuestions?: {value: string; label: string}[];
}) {
    const {
        name,
        phone,
        email,
        rooms,
        generatedMessage,
        questions,
        analyticsData,
        analyticsParams,
        originalText,
        leadResponse,
        leadData,
        leadMeta,
        leadModalType,
        affiliation,
        negotiation,
        meetingDay,
        additionalQuestions
    } = trackingParams;
    const {sourceSection, locationHash, viewType} = leadMeta;
    const phoneWithoutSpace = phone.replace(" ", "");

    if (generatedMessage && generatedMessage !== originalText) {
        sendApplicationHit(questions, name, phoneWithoutSpace, email, analyticsData, generatedMessage, originalText);
    } else {
        sendApplicationHit(questions, name, phoneWithoutSpace, email, analyticsData, generatedMessage ? generatedMessage : "");
    }

    hitLeadSuccess({
        locationHash,
        viewType,
        rooms,
        leadResponse,
        sourceSection,
        leadData
    });

    let eventLabel = "zapytaj";

    if (negotiation) {
        eventLabel = "negocjuj cenę";
    }
    if (meetingDay) {
        eventLabel = "umów spotkanie";
    }

    let eventCategory: string | undefined;
    if (additionalQuestions && additionalQuestions.length > 0) {
        eventCategory = additionalQuestions.map((question) => question.value).join(", ");
    }

    hitGtmAfterSuccess(leadResponse, leadData, leadModalType, viewType, affiliation, analyticsParams, eventLabel, eventCategory);

    if (analyticsParams?.amplitudeLeadFormEventTracking) {
        trackAmplitudeLeadFormGenerateLead({
            trackingContext: analyticsParams.amplitudeLeadFormEventTracking,
            leadData,
            source: leadMeta.source,
            sourceSection: leadMeta.sourceSection,
            leadResponse,
            inquirySelection: {
                negotiation,
                additionalQuestions
            },
            ...(analyticsParams.amplitudeLeadFormSubmitContext?.eventAction ? {eventAction: analyticsParams.amplitudeLeadFormSubmitContext.eventAction} : {}),
            ...(analyticsParams.amplitudeLeadFormSubmitContext?.ctaText ? {ctaText: analyticsParams.amplitudeLeadFormSubmitContext.ctaText} : {}),
            ...(analyticsParams.amplitudeLeadFormSubmitContext?.isPriceNegotiation ? {isPriceNegotiation: true} : {})
        });
    }

    facebookPixelPurchase(leadData);
}

function hitGtmAfterSuccess(
    leadResponse: IApplicationData,
    leadData: IHitApplicationData,
    leadType: ILeadModalLeadType,
    viewType: ViewType | null,
    affiliation?: string,
    analyticsParams?: ISendLeadParams["analyticsParams"],
    eventLabel?: string,
    eventCategory?: string
) {
    if (!leadData) {
        throw new Error("hitGtmAfterSuccess: application data should be filled already");
    }

    gtmInquiryOnApplicationSuccess(leadResponse, leadData, viewType, leadType, false, affiliation, analyticsParams?.customSourceSection, eventLabel);
    hitGTMEcommercePurchase(leadData, viewType, leadType, analyticsParams?.customSourceSection ?? leadResponse.source_section, leadResponse, eventCategory);

    if (leadResponse.user_created) {
        gtmEventApplicationRegister();
    }
}
import {ICalendarDate} from "@pg-design/calendar-module";
import {isNumber} from "@pg-mono/nodash";
import {UserComEventNames} from "@pg-mono/user-com";
import {Currency} from "@pg-rp/common-types";

import {getClientStore} from "../../../../client/utils/get_client_store";
import {createRpLeadPerformanceDebugTrace} from "../../../app/performance_debug";
import {authSucceeded} from "../../../auth/actions/auth_succeeded_event";
import {AuthActionType} from "../../../auth/types/AuthActionType";
import {addAppliedOffers} from "../../../offer/map/actions/add_applied_offers";
import {hitGTMEcommercePurchase} from "../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_purchase";
import {IOfferDetailApplication} from "../../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../../property/types/IPropertyDetailApplication";
import {IAppDispatch} from "../../../store/store";
import {awinHit} from "../../../tracking/awin_tracking/awin_conversion_tag";
import {facebookPixelPurchase} from "../../../tracking/facebook_pixel/facebook_pixel_purchase";
import {gtmEventApplicationRegister} from "../../../tracking/google_tag_manager/gtm_event_application_register";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {patchUserProfileFromApplication} from "../../../user/actions/patch_user_profile_from_application";
import {IUserProfile} from "../../../user/types/IUserProfile";
import {ViewType} from "../../../view_type/ViewType";
import {afterPostApplicationActions} from "../../actions/after_post_application";
import {createApplicationApi} from "../../api/create_application";
import {trackAmplitudeLeadFormGenerateLead} from "../../tracking/amplitude/track_amplitude_lead_form_events";
import {IApplicationAnalyticsData, sendApplicationHit} from "../../tracking/application_form_tracking";
import {gtmInquiryOnApplicationSuccess} from "../../tracking/lead/gtm_inquiry_on_application_success";
import {hitLeadSuccess} from "../../tracking/lead/hit_lead_success";
import {IApplicationAdditionalQuestionTypes} from "../../types/form/IApplicationFormAdditionalQuestionTypes";
import {IApplicationOptions} from "../../types/form/IApplicationFormOptions";
import {IApplicationFormValues} from "../../types/form/IApplicationFormValues";
import {IApplicationData} from "../../types/IApplicationData";
import {IAmplitudeLeadFormSubmitContext, IAmplitudeLeadFormTrackingContext} from "../../types/lead/IAmplitudeLeadFormEventTracking";
import {IHitApplicationData} from "../../types/lead/IHitApplicationData";
import {ILeadModalLeadType} from "../../types/lead/ILeadModalLeadType";
import {ILeadType} from "../../types/lead/ILeadType";
import {IPostApplicationPayload} from "../../types/lead/IPostApplicationPayload";
import {IRealEstateIds} from "../../types/lead/IRealEstateIds";
import {addPropertiesToStorageSentApplication} from "../application_property_storage";
import {ApplicationSource, ApplicationSourceSection} from "../form/application_form_source";
import {handlePostApplicationResponse} from "../form/handle_post_application_response";
import {addOffersToStorageSentApplication} from "../multi_lead/get_storage_sent_applications_id_list_by";
import {getParsedLeadFormValues} from "./get_parsed_lead_form_values";
import {lead} from "./lead";
import {updateUserComAfterLead} from "./update_user_com_after_lead";

export type ISendLeadParams = {
    leadData: IHitApplicationData;
    leadFormValues: IApplicationFormValues;
    leadMeta: ILeadMeta;
    isAuthenticated: boolean;
    userProfileData?: IUserProfile | null;
    senderState: {
        /**
         * TODO generatedMessage is not being generate in Application anymore,
         * but i'm not removing it since it's used as an input in other usages
         */
        generatedMessage: string | null;
        questions: IApplicationAdditionalQuestionTypes[];
    };
    analyticsData: IApplicationAnalyticsData;
    analyticsParams?: {
        customSourceSection?: string;
        amplitudeLeadFormEventTracking?: IAmplitudeLeadFormTrackingContext | null;
        amplitudeLeadFormSubmitContext?: IAmplitudeLeadFormSubmitContext;
    };
    affiliation?: string;
    realEstateIds?: IRealEstateIds;
    postOptions?: IApplicationOptions;
    creditData?: {price: number; contribution: number; period: number} | null;
    onSendSuccess?: (response: IApplicationData) => void;
    showSuccessNotification?: boolean;
    leadType: ILeadModalLeadType;
    dispatch: IAppDispatch;
};

export interface ILeadMeta {
    source: ApplicationSource;
    sourceSection: ApplicationSourceSection;
    locationHash: string;
    viewType: ViewType | null;
}

export async function sendLead(params: ISendLeadParams) {
    const {
        leadFormValues,
        leadMeta,
        leadData,
        isAuthenticated,
        senderState,
        analyticsData,
        analyticsParams,
        affiliation,
        postOptions,
        onSendSuccess,
        realEstateIds,
        userProfileData,
        creditData,
        showSuccessNotification = true,
        leadType,
        dispatch
    } = params;
    const trace = createRpLeadPerformanceDebugTrace("rp_send_lead", {
        leadModalType: leadType,
        leadType: lead.getLeadType(realEstateIds?.vendorId || null, realEstateIds?.offerId || null, realEstateIds?.propertyId || null),
        offerId: realEstateIds?.offerId,
        propertyId: realEstateIds?.propertyId,
        source: leadMeta.source,
        sourceSection: leadMeta.sourceSection,
        vendorId: realEstateIds?.vendorId
    });

    const {offer, property} = leadData;

    trace.mark("payload:build_start");
    const leadPayload = getLeadPayloadData({
        leadFormValues,
        leadMeta,
        offer,
        realEstateIds,
        creditData
    });
    trace.mark("payload:build_done", {
        hasFastcall: Boolean(leadPayload.fastcall),
        hasFinancingServices: Boolean(leadPayload.financing_services),
        hasOffer: typeof leadPayload.offer === "number",
        hasProperty: typeof leadPayload.property === "number",
        hasVendor: typeof leadPayload.vendor === "number"
    });

    trace.mark("create_application:dispatch_start");
    const applicationResponse = await dispatch(createApplicationApi.endpoints.createApplication.initiate(leadPayload));
    trace.mark("create_application:dispatch_done", {
        hasError: "error" in applicationResponse,
        hasData: "data" in applicationResponse
    });

    trace.mark("post_application_response:handle_start", {hasPostOptions: Boolean(postOptions)});
    const sentLeadRes = postOptions && (await handlePostApplicationResponse(applicationResponse, 1, dispatch, leadPayload, postOptions));
    trace.mark("post_application_response:handle_done", {hasSentLeadResponse: Boolean(sentLeadRes)});

    if (sentLeadRes && onSendSuccess) {
        trace.mark("on_send_success:start");
        onSendSuccess(sentLeadRes);
        trace.mark("on_send_success:done");
    }

    if (sentLeadRes) {
        const {generatedMessage, questions} = senderState;

        trace.mark("send_success_actions:start");
        await handleSendSuccess({
            response: sentLeadRes,
            offer: offer?.detail,
            property: property?.detail,
            isAuthenticated,
            userProfileData,
            showSuccessNotification
        });
        trace.mark("send_success_actions:done", {
            isAuthenticated,
            userCreated: Boolean(sentLeadRes.user_created)
        });

        trace.mark("lead_tracking:start");
        handleLeadTracking({
            name: leadFormValues.name,
            /*
                TODO: Create separate interface for applicationPost, so we won't have to use Partial<IApplication>
                    this will enable to remove "as string" below and will be a better approach in general
                    Application serializer to use for that - python-pg/-/blob/master/rpm/applications/serializers.py
             */
            phone: leadPayload.phone as string,
            email: leadFormValues.email,
            user_id: userProfileData?.uuid || null,
            rooms: leadFormValues.rooms,
            generatedMessage,
            questions,
            analyticsData,
            analyticsParams,
            originalText: leadFormValues.text || "",
            leadResponse: sentLeadRes,
            leadData,
            leadMeta,
            leadType: lead.getLeadType(realEstateIds?.vendorId || null, realEstateIds?.offerId || null, realEstateIds?.propertyId || null),
            leadModalType: leadType,
            affiliation,
            negotiation: leadFormValues.negotiation,
            meetingDay: leadFormValues.meetingDay,
            additionalQuestions: leadFormValues.additionalQuestions
        });
        trace.mark("lead_tracking:done");

        trace.mark("user_com_update:start");
        updateUserComAfterLead({
            eventName: UserComEventNames.UserLead,
            sentLead: {
                email: leadFormValues.email,
                phone: leadPayload.phone as string
            },
            userProfile: userProfileData
        });
        trace.mark("user_com_update:done");

        trace.mark("awin_hit:start");
        awinHit({
            applicationId: sentLeadRes.uuid,
            currency: offer?.detail?.currency || property?.detail?.currency || Currency.PLN
        });
        trace.mark("awin_hit:done");
    }

    trace.end("send_lead:done", {hasSentLeadResponse: Boolean(sentLeadRes)});
    return sentLeadRes;
}

interface IGetLeadPayloadParams {
    leadFormValues: IApplicationFormValues;
    leadMeta: ILeadMeta;
    offer: IHitApplicationData["offer"];
    realEstateIds?: IRealEstateIds;
    creditData?: {price: number; contribution: number; period: number} | null;
}

function getLeadPayloadData(params: IGetLeadPayloadParams) {
    const {leadFormValues, leadMeta, offer, realEstateIds, creditData} = params;
    const {source, sourceSection} = leadMeta;

    const parsedLeadFormValues = getParsedLeadFormValues(leadFormValues);

    const applicationData: IPostApplicationPayload = {
        source,
        source_section: sourceSection,
        ...parsedLeadFormValues
    };

    if (creditData) {
        applicationData.financing_services = true;
        applicationData.term_of_the_loan = creditData.period;
        applicationData.calculator_estimated_property_price = creditData.price;
        applicationData.own_contribution = creditData.contribution;
    }

    if (realEstateIds && realEstateIds.salesOfficeId) {
        // variant: 1 disables backend validation of 'meeting_time_preference' field
        applicationData.variant = 1;
        applicationData.sales_office = realEstateIds.salesOfficeId;
    }

    if (source === ApplicationSource.ApplicationPropertyScheduleMeeting) {
        // variant: 2 disables backend validation of 'sales_office' and 'meeting_time_preference' fields
        applicationData.variant = 2;
    }

    if (realEstateIds && isNumber(realEstateIds.propertyId)) {
        applicationData.property = realEstateIds.propertyId;
    } else if (realEstateIds && isNumber(realEstateIds.offerId)) {
        applicationData.offer = realEstateIds.offerId;
    } else if (realEstateIds && isNumber(realEstateIds.vendorId)) {
        applicationData.vendor = realEstateIds.vendorId;
    }

    if (offer?.detail?.configuration?.fastcall_uid) {
        applicationData.fastcall = true;
    }

    return applicationData;
}

async function handleSendSuccess(props: {
    response: IApplicationData;
    offer?: Pick<IOfferDetailApplication, "id" | "geo_point" | "region">;
    property?: Pick<IPropertyDetailApplication, "id">;
    isAuthenticated: boolean;
    userProfileData?: IUserProfile | null;
    showSuccessNotification: boolean;
}) {
    const store = getClientStore();

    if (!store) {
        throw new Error("sendLead: store is not available");
    }

    if (props.showSuccessNotification) {
        afterPostApplicationActions()(store.dispatch);
    }

    if (props.offer) {
        addOffersToStorageSentApplication([
            {
                id: props.offer.id,
                coordinates: props.offer.geo_point.coordinates,
                country: props.offer.region.country,
                regionId: props.offer.region.parent?.id
            }
        ]);
    }

    if (props.property) {
        addPropertiesToStorageSentApplication([props.property.id]);
    }

    // user was created when submitting the application - try to log in
    if (props.response.user_created) {
        store.dispatch(
            authSucceeded({
                type: AuthActionType.REGISTRATION,
                userSource: UserSource.APPLICATION,
                registrationMethod: "email"
            })
        );
    }
    // try to update logged-in user data
    if (props.isAuthenticated && props.userProfileData && !props.response.user_created) {
        await store.dispatch(patchUserProfileFromApplication(props.userProfileData, props.response));
    }

    if (props.isAuthenticated) {
        store.dispatch(addAppliedOffers([props.response.offer]));
    }
}

/**
 * Handles tracking after successful lead post/submit
 *
 * @param trackingParams
 * questions - form questions, but from component state, not the form itself
 * generatedMessage - a "rotating" message, but from component state, not the form itself
 * originalText - not modified text, taken directly from form values
 */
function handleLeadTracking(trackingParams: {
    name: string;
    phone: string;
    email: string;
    user_id: string | null;
    rooms?: number[];
    questions: IApplicationAdditionalQuestionTypes[];
    generatedMessage: string | null;
    originalText: string;
    analyticsData: IApplicationAnalyticsData;
    analyticsParams?: ISendLeadParams["analyticsParams"];
    leadResponse: IApplicationData;
    leadData: IHitApplicationData;
    leadMeta: ILeadMeta;
    leadType: ILeadType;
    leadModalType: ILeadModalLeadType;
    affiliation?: string;
    negotiation?: number | null;
    meetingDay?: ICalendarDate;
    additionalQuestions?: {value: string; label: string}[];
}) {
    const {
        name,
        phone,
        email,
        rooms,
        generatedMessage,
        questions,
        analyticsData,
        analyticsParams,
        originalText,
        leadResponse,
        leadData,
        leadMeta,
        leadModalType,
        affiliation,
        negotiation,
        meetingDay,
        additionalQuestions
    } = trackingParams;
    const {sourceSection, locationHash, viewType} = leadMeta;
    const phoneWithoutSpace = phone.replace(" ", "");

    if (generatedMessage && generatedMessage !== originalText) {
        sendApplicationHit(questions, name, phoneWithoutSpace, email, analyticsData, generatedMessage, originalText);
    } else {
        sendApplicationHit(questions, name, phoneWithoutSpace, email, analyticsData, generatedMessage ? generatedMessage : "");
    }

    hitLeadSuccess({
        locationHash,
        viewType,
        rooms,
        leadResponse,
        sourceSection,
        leadData
    });

    let eventLabel = "zapytaj";

    if (negotiation) {
        eventLabel = "negocjuj cenę";
    }
    if (meetingDay) {
        eventLabel = "umów spotkanie";
    }

    let eventCategory: string | undefined;
    if (additionalQuestions && additionalQuestions.length > 0) {
        eventCategory = additionalQuestions.map((question) => question.value).join(", ");
    }

    hitGtmAfterSuccess(leadResponse, leadData, leadModalType, viewType, affiliation, analyticsParams, eventLabel, eventCategory);

    if (analyticsParams?.amplitudeLeadFormEventTracking) {
        trackAmplitudeLeadFormGenerateLead({
            trackingContext: analyticsParams.amplitudeLeadFormEventTracking,
            leadData,
            source: leadMeta.source,
            sourceSection: leadMeta.sourceSection,
            leadResponse,
            inquirySelection: {
                negotiation,
                additionalQuestions
            },
            ...(analyticsParams.amplitudeLeadFormSubmitContext?.eventAction ? {eventAction: analyticsParams.amplitudeLeadFormSubmitContext.eventAction} : {}),
            ...(analyticsParams.amplitudeLeadFormSubmitContext?.ctaText ? {ctaText: analyticsParams.amplitudeLeadFormSubmitContext.ctaText} : {}),
            ...(analyticsParams.amplitudeLeadFormSubmitContext?.isPriceNegotiation ? {isPriceNegotiation: true} : {})
        });
    }

    facebookPixelPurchase(leadData);
}

function hitGtmAfterSuccess(
    leadResponse: IApplicationData,
    leadData: IHitApplicationData,
    leadType: ILeadModalLeadType,
    viewType: ViewType | null,
    affiliation?: string,
    analyticsParams?: ISendLeadParams["analyticsParams"],
    eventLabel?: string,
    eventCategory?: string
) {
    if (!leadData) {
        throw new Error("hitGtmAfterSuccess: application data should be filled already");
    }

    gtmInquiryOnApplicationSuccess(leadResponse, leadData, viewType, leadType, false, affiliation, analyticsParams?.customSourceSection, eventLabel);
    hitGTMEcommercePurchase(leadData, viewType, leadType, analyticsParams?.customSourceSection ?? leadResponse.source_section, leadResponse, eventCategory);

    if (leadResponse.user_created) {
        gtmEventApplicationRegister();
    }
}
